import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, fetchConfig, resetShimmer } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Collection-level quick order: variant rows, client filters, and cart updates.
 *
 * @typedef {object} QuickOrderCollectionRefs
 * @property {HTMLElement} [gridBody]
 * @property {HTMLElement} [productCountText]
 * @property {HTMLElement} [asideItemCount]
 * @property {HTMLElement} [asideTotalPrice]
 * @property {HTMLElement} [asideCartLink]
 * @property {HTMLElement} [footerItemCount]
 * @property {HTMLElement} [footerCartLink]
 * @property {HTMLElement} [removeAllButton]
 * @property {HTMLElement} [totalPrice]
 * @property {HTMLElement} confirmationPanel
 * @property {HTMLElement} totalInfo
 * @property {HTMLElement} errorContainer
 * @property {HTMLElement} errorText
 * @property {HTMLElement} successContainer
 * @property {HTMLElement} successText
 * @property {HTMLElement} [loadMoreButton]
 * @property {HTMLElement} [emptyState]
 * @property {HTMLElement} [emptyStateText]
 * @property {HTMLElement} [clearFiltersButton]
 * @property {HTMLElement} [floatingSummary]
 * @property {HTMLElement} [floatingItemCount]
 * @property {HTMLElement} [floatingTotalPrice]
 * @property {HTMLElement} [floatingCartLink]
 * @property {HTMLElement} [asideTotals]
 * @property {HTMLElement} [confirmationText]
 *
 * @extends Component<QuickOrderCollectionRefs>
 */
class QuickOrderCollectionComponent extends Component {
  requiredRefs = ['confirmationPanel', 'totalInfo', 'errorContainer', 'errorText', 'successContainer', 'successText'];

  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {(event: Event) => void} */
  #debouncedHandleQuantityUpdate;

  /** @type {(event: CartLinesUpdateEvent) => void} */
  #boundHandleCartUpdate;

  /** @type {HTMLElement|null} */
  #confirmationTrigger = null;

  /** @type {IntersectionObserver|null} */
  #floatingObserver = null;

  /** @type {(() => void) | undefined} */
  #debouncedLoadRemaining;

  /** @type {AbortController|null} */
  #filterLoadController = null;

  #filterGeneration = 0;

  #loadingMore = false;

  #autoLoadActive = false;

  /** @type {{ value: string, label: string }[]} */
  #allTypeOptions = [];

  #allTypesLabel = '';

  get cartVariantIds() {
    const data = this.dataset.cartVariantIds;
    if (!data) return [];
    return JSON.parse(data);
  }

  get cartLineKeys() {
    const data = this.dataset.cartLineKeys;
    if (!data) return [];
    return JSON.parse(data);
  }

  /** @returns {HTMLElement[]} */
  #rows() {
    return Array.from(this.querySelectorAll('.quick-order-collection__row'));
  }

  connectedCallback() {
    super.connectedCallback();

    this.#debouncedHandleQuantityUpdate = debounce(this.#handleQuantityUpdate.bind(this), 300);
    this.#debouncedLoadRemaining = debounce(this.#loadRemainingForFilters.bind(this), 300);
    this.#boundHandleCartUpdate = this.#handleCartUpdate.bind(this);

    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedHandleQuantityUpdate);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#boundHandleCartUpdate);
    this.addEventListener('keydown', this.#handleKeyDown, true);
    this.addEventListener('click', this.#handleDelegatedClick);

    this.groupProductsByType();
    this.#cacheTypeOptions();
    this.applyFilters();
    this.#observeFloatingSummary();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedHandleQuantityUpdate);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#boundHandleCartUpdate);
    this.removeEventListener('keydown', this.#handleKeyDown, true);
    this.removeEventListener('click', this.#handleDelegatedClick);
    this.#floatingObserver?.disconnect();
    this.#abortController?.abort();
    this.#cancelFilterLoading();
    this.#confirmationTrigger = null;
  }

  /**
   * @param {EventTarget | null} target
   * @returns {target is HTMLInputElement}
   */
  #isQuantityInput(target) {
    return target instanceof HTMLInputElement && target.matches('input[type="number"][data-cart-quantity]');
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Enter' || !this.#isQuantityInput(event.target)) return;
    event.preventDefault();

    const inputs = Array.from(this.querySelectorAll('input[type="number"][data-cart-quantity]')).filter((input) => {
      return input instanceof HTMLElement && input.offsetParent !== null;
    });
    if (inputs.length <= 1) return;

    const currentIndex = inputs.indexOf(event.target);
    if (currentIndex === -1) return;

    const nextInput = inputs[(currentIndex + (event.shiftKey ? -1 : 1) + inputs.length) % inputs.length];
    event.target.blur();
    if (nextInput instanceof HTMLInputElement) nextInput.select();
  };

  handleSubmit = (event) => {
    event.preventDefault();
  };

  /**
   * @param {Event} event
   */
  #handleDelegatedClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const addButton = target.closest('.variant-item__add-to-quote');
    if (addButton instanceof HTMLElement && this.contains(addButton)) {
      this.addToQuote({ currentTarget: addButton });
      return;
    }

    const removeButton = target.closest('.variant-item__remove');
    if (removeButton instanceof HTMLElement && this.contains(removeButton)) {
      const variantId = removeButton.closest('[data-variant-id]')?.dataset.variantId;
      if (variantId) this.onLineItemRemove(variantId, event);
    }
  };

  /**
   * @param {Event} event
   */
  addToQuote = (event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;

    const variantRow = target.closest('.quick-order-collection__row');
    if (!(variantRow instanceof HTMLElement)) return;

    const plusButtons = Array.from(variantRow.querySelectorAll('button[name="plus"]'));
    const plusButton =
      plusButtons.find((button) => button instanceof HTMLButtonElement && button.offsetParent !== null) ||
      plusButtons[0];
    if (plusButton instanceof HTMLButtonElement) {
      plusButton.click();
    }
  };

  /**
   * @param {string} variantId
   * @param {Event} event
   */
  async onLineItemRemove(variantId, event) {
    event.preventDefault();
    const targetRow = this.#rows().find((row) => row.dataset.variantId === String(variantId));
    if (!(targetRow instanceof HTMLElement)) return;

    const quantityInput = targetRow.querySelector('input[type="number"]');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = '0';
      quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(0, Number(quantityInput.dataset.cartLine)));
    }
  }

  applyFilters = () => {
    this.#syncTypeOptions();
    this.#filterRows();
    this.#updateProductCount();
    this.#cancelFilterLoading();
    this.#updateEmptyState();

    if (this.#shouldLoadRemaining()) {
      this.#debouncedLoadRemaining?.();
    }
  };

  /**
   * @param {Event} [event]
   */
  clearFilters = (event) => {
    event?.preventDefault();
    this.#cancelFilterLoading();

    const vendor = this.querySelector('select[name="product-vendor"]');
    const type = this.querySelector('select[name="product-type"]');
    const search = this.querySelector('input[name="product-search"]');
    if (vendor instanceof HTMLSelectElement) vendor.value = '';
    if (type instanceof HTMLSelectElement) type.value = '';
    if (search instanceof HTMLInputElement) search.value = '';

    this.#syncTypeOptions();
    this.#filterRows();
    this.#updateProductCount();
    this.#updateEmptyState();
  };

  groupProductsByType() {
    const gridBody = this.refs.gridBody || this.querySelector('.quick-order-list__grid-body');
    if (!(gridBody instanceof HTMLElement)) return;

    const rows = this.#rows();
    for (const header of gridBody.querySelectorAll('.quick-order-collection__type-header')) {
      header.remove();
    }

    /** @type {Record<string, HTMLElement[]>} */
    const grouped = {};
    for (const row of rows) {
      const type = row.dataset.productType || '';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(row);
    }

    const types = Object.keys(grouped).sort((a, b) => {
      if (a === '') return 1;
      if (b === '') return -1;
      return a.localeCompare(b);
    });

    const fragment = document.createDocumentFragment();
    for (const type of types) {
      if (type) {
        const header = document.createElement('div');
        header.className = 'quick-order-collection__type-header';
        header.dataset.productType = type;
        header.setAttribute('role', 'row');
        const label = document.createElement('div');
        label.className = 'quick-order-list__grid-cell';
        label.textContent = type;
        header.appendChild(label);
        fragment.appendChild(header);
      }
      for (const row of grouped[type] || []) {
        fragment.appendChild(row);
      }
    }

    gridBody.appendChild(fragment);
  }

  /**
   * @param {Event} event
   */
  async loadMore(event) {
    event.preventDefault();
    const loaded = await this.#fetchNextPage();
    if (!loaded) return;

    this.groupProductsByType();
    this.#syncTypeOptions();
    this.#filterRows();
    this.#updateProductCount();
    this.#updateEmptyState();

    if (this.#shouldLoadRemaining()) {
      this.#debouncedLoadRemaining?.();
    }
  }

  /**
   * @param {Event} event
   */
  showRemoveAllConfirmation(event) {
    event.preventDefault();
    this.#confirmationTrigger = /** @type {HTMLElement} */ (event.target);
    this.#toggleConfirmationPanel(true);
  }

  /**
   * @param {Event} event
   */
  hideRemoveAllConfirmation(event) {
    event.preventDefault();
    this.#toggleConfirmationPanel(false);
    this.#confirmationTrigger?.focus();
    this.#confirmationTrigger = null;
  }

  /**
   * @param {Event} event
   */
  async onRemoveAll(event) {
    event.preventDefault();
    const idsToRemove = this.cartVariantIds;
    this.#clearSuccessMessage();
    this.#clearErrorMessage();
    this.#applyShimmerEffects(idsToRemove);
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    /** @type {Record<string, number>} */
    const updates = {};
    for (const variantId of idsToRemove) {
      updates[String(variantId)] = 0;
    }

    if (Object.keys(updates).length === 0) {
      resetShimmer(this);
      return;
    }

    await this.#submitCartUpdates(updates, 'remove', this.cartLineKeys.map((key) => ({ id: key, quantity: 0 })), {
      source: 'quick-order-collection-remove-all',
    });
    this.#toggleConfirmationPanel(false);
    this.#confirmationTrigger = null;
  }

  /**
   * @param {CustomEvent} event
   */
  async #handleQuantityUpdate(event) {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity } = event.detail;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const variantRow = target.closest('.quick-order-collection__row');
    if (!(variantRow instanceof HTMLElement)) return;

    const variantId = variantRow.dataset.variantId;
    if (!variantId) return;

    const quantityInput = /** @type {HTMLInputElement|null} */ (variantRow.querySelector('input[data-cart-quantity]'));
    const currentCartQuantity = quantityInput ? parseInt(quantityInput.dataset.cartQuantity || '0', 10) || 0 : 0;
    if (currentCartQuantity === quantity) return;

    this.#clearSuccessMessage();
    this.#clearErrorMessage();
    this.#applyShimmerEffects([variantId]);

    /** @type {'add' | 'remove' | 'update'} */
    let action = 'update';
    if (currentCartQuantity === 0) action = 'add';
    else if (quantity === 0) action = 'remove';

    const lineKey = variantRow.dataset.cartLineKey ?? '';
    await this.#submitCartUpdates({ [variantId]: quantity }, action, action === 'add'
      ? [{ merchandiseId: variantId, quantity }]
      : [{ id: lineKey, quantity }], {
      source: 'quick-order-collection-quantity',
      variantId,
      quantityAdded: quantity - currentCartQuantity,
    });
  }

  /**
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate(event) {
    this.#enableItems();
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    event.promise
      ?.then(async ({ cart, detail }) => {
        if (
          detail?.source === 'quick-order-collection-quantity' ||
          detail?.source === 'quick-order-collection-remove-all'
        ) {
          return;
        }

        if (cart) {
          this.#updateFromCart(cart);
          return;
        }

        const response = await fetch(`${Theme.routes.cart_url}.js`);
        const data = await response.json();
        this.#updateFromCart(data);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-order-collection] Event promise rejected:', error);
      });
  }

  /**
   * @param {Record<string, number>} updates
   * @param {'add' | 'remove' | 'update'} action
   * @param {Array<Record<string, unknown>>} lines
   * @param {{ source: string, variantId?: string, quantityAdded?: number }} meta
   */
  async #submitCartUpdates(updates, action, lines, meta) {
    this.#disableItems();
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        context: 'cart',
        action,
        lines,
        promise: deferredPromise.promise,
      })
    );

    try {
      const body = JSON.stringify({
        updates,
        sections: this.#getCartSectionIds().join(','),
      });
      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body }),
        signal: this.#abortController.signal,
      });
      const data = JSON.parse(await response.text());
      resetShimmer(this);

      if (data.errors) {
        this.#showErrorMessage(typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors));
        deferredPromise.reject(new Error(String(data.errors)));
        this.dispatchEvent(new CartErrorEvent({ error: String(data.errors), code: 'INVALID' }));
        this.#enableItems();
        return;
      }

      deferredPromise.resolve(
        /** @type {any} */ ({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
          detail: {
            sections: data.sections,
            source: meta.source,
            variantId: meta.variantId,
            didError: false,
          },
        })
      );

      this.#updateFromCart(data);
      this.#updateCartSections(data.sections);
      this.#enableItems();

      if (meta.quantityAdded && meta.quantityAdded > 0) {
        this.#showSuccessMessage(meta.quantityAdded);
      }
    } catch (error) {
      deferredPromise.reject(error);
      if (error.name !== 'AbortError') {
        this.#enableItems();
        resetShimmer(this);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    }
  }

  /**
   * @param {{ items?: Array<Record<string, any>>, item_count?: number, total_price?: number }} cart
   */
  #updateFromCart(cart) {
    /** @type {Map<number, { quantity: number, key: string, line_price: number }>} */
    const byVariant = new Map();
    for (const item of cart.items || []) {
      const current = byVariant.get(item.variant_id);
      if (current) {
        current.quantity += item.quantity;
        current.line_price += item.original_line_price || item.line_price || 0;
      } else {
        byVariant.set(item.variant_id, {
          quantity: item.quantity,
          key: item.key,
          line_price: item.original_line_price || item.line_price || 0,
        });
      }
    }

    this.dataset.cartVariantIds = JSON.stringify([...byVariant.keys()]);
    this.dataset.cartLineKeys = JSON.stringify([...byVariant.values()].map((item) => item.key));

    const hidePrices = this.classList.contains('quick-order-list--hide-prices');

    for (const row of this.#rows()) {
      const variantId = Number(row.dataset.variantId);
      const item = byVariant.get(variantId);
      const quantity = item?.quantity ?? 0;
      row.dataset.cartLineKey = item?.key || '';

      for (const input of row.querySelectorAll('input[data-cart-quantity]')) {
        if (!(input instanceof HTMLInputElement)) continue;
        input.value = String(quantity);
        input.dataset.cartQuantity = String(quantity);
      }

      for (const button of row.querySelectorAll('.variant-item__remove')) {
        button.classList.toggle('variant-item__remove--hidden', quantity === 0);
        if (quantity === 0) {
          button.setAttribute('tabindex', '-1');
          button.setAttribute('aria-hidden', 'true');
        } else {
          button.removeAttribute('tabindex');
          button.removeAttribute('aria-hidden');
        }
      }

      if (!hidePrices && item) {
        const total = row.querySelector('.variant-item__total-price');
        if (total) total.textContent = this.#formatMoney(item.line_price);
      } else if (!hidePrices) {
        const total = row.querySelector('.variant-item__total-price');
        if (total) total.textContent = this.#formatMoney(0);
      }
    }

    const itemCount = cart.item_count ?? 0;
    this.#setText(this.refs.asideItemCount, String(itemCount));
    this.#setText(this.refs.footerItemCount, String(itemCount));
    this.#setText(this.refs.floatingItemCount, this.#pluralize(itemCount, this.dataset.itemCountOne, this.dataset.itemCountOther));

    if (!hidePrices) {
      const formatted = this.#formatMoney(cart.total_price);
      this.#setText(this.refs.asideTotalPrice, formatted);
      this.#setText(this.refs.floatingTotalPrice, formatted);
      if (this.refs.totalPrice) this.refs.totalPrice.textContent = formatted;
    }

    this.#toggleCartLinks(itemCount > 0);
    this.refs.removeAllButton?.classList.toggle('hidden', itemCount === 0);

    if (this.refs.confirmationText) {
      this.refs.confirmationText.textContent =
        itemCount > 1
          ? (this.dataset.removeAllConfirmation || '').replaceAll('__COUNT__', String(itemCount))
          : this.dataset.removeOneConfirmation || this.refs.confirmationText.textContent;
    }
  }

  /**
   * @param {Record<string, string> | undefined} sections
   */
  #updateCartSections(sections) {
    if (!sections) return;
    for (const [sectionId, html] of Object.entries(sections)) {
      if (sectionId && html) morphSection(sectionId, html);
    }
  }

  #getCartSectionIds() {
    const sectionIds = [];
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (!(component instanceof HTMLElement)) continue;
      if (component.dataset.sectionId && !sectionIds.includes(component.dataset.sectionId)) {
        sectionIds.push(component.dataset.sectionId);
      }
    }
    return sectionIds;
  }

  #filterRows() {
    const { vendor: selectedVendor, type: selectedType, search: searchTerm } = this.#getFilterState();
    const visibleTypes = new Set();

    for (const row of this.#rows()) {
      const matchesVendor = selectedVendor === '' || row.dataset.productVendor === selectedVendor;
      const matchesType = selectedType === '' || row.dataset.productType === selectedType;
      const haystack = this.#normalizeSearchValue(
        [row.dataset.productTitle, row.dataset.variantTitle, row.dataset.sku, row.dataset.productType, row.dataset.productVendor].join(
          ' '
        )
      );
      const matchesSearch = searchTerm === '' || haystack.includes(searchTerm);
      const visible = matchesVendor && matchesType && matchesSearch;
      row.hidden = !visible;
      if (visible && row.dataset.productType) visibleTypes.add(row.dataset.productType);
    }

    for (const header of this.querySelectorAll('.quick-order-collection__type-header')) {
      if (!(header instanceof HTMLElement)) continue;
      const headerType = header.dataset.productType || '';
      const typeMatches = selectedType === '' || selectedType === headerType;
      header.hidden = !(typeMatches && visibleTypes.has(headerType));
    }
  }

  #cacheTypeOptions() {
    const typeSelect = this.querySelector('select[name="product-type"]');
    if (!(typeSelect instanceof HTMLSelectElement)) return;

    this.#allTypesLabel = typeSelect.options[0]?.textContent || '';
    this.#allTypeOptions = Array.from(typeSelect.options)
      .filter((option) => option.value)
      .map((option) => ({ value: option.value, label: option.textContent || option.value }));
  }

  #syncTypeOptions() {
    const typeSelect = this.querySelector('select[name="product-type"]');
    if (!(typeSelect instanceof HTMLSelectElement)) return;

    const vendor = this.querySelector('select[name="product-vendor"]');
    const selectedVendor = vendor instanceof HTMLSelectElement ? vendor.value : '';
    const currentType = typeSelect.value;
    const options = selectedVendor ? this.#typesForVendor(selectedVendor) : this.#allTypeOptions;
    const nextValues = options.map((option) => option.value);
    const currentValues = Array.from(typeSelect.options)
      .slice(1)
      .map((option) => option.value);
    const optionsChanged =
      nextValues.length !== currentValues.length || nextValues.some((value, index) => value !== currentValues[index]);

    if (optionsChanged) {
      const fragment = document.createDocumentFragment();
      const allOption = document.createElement('option');
      allOption.value = '';
      allOption.textContent = this.#allTypesLabel;
      fragment.appendChild(allOption);

      for (const option of options) {
        const item = document.createElement('option');
        item.value = option.value;
        item.textContent = option.label;
        fragment.appendChild(item);
      }

      typeSelect.replaceChildren(fragment);
    }

    typeSelect.value = currentType && nextValues.includes(currentType) ? currentType : '';
  }

  /**
   * @param {string} vendor
   */
  #typesForVendor(vendor) {
    const types = new Set();
    for (const row of this.#rows()) {
      const type = row.dataset.productType || '';
      if (type && row.dataset.productVendor === vendor) types.add(type);
    }

    return [...types]
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }

  #getFilterState() {
    const vendor = this.querySelector('select[name="product-vendor"]');
    const type = this.querySelector('select[name="product-type"]');
    const search = this.querySelector('input[name="product-search"]');

    return {
      vendor: vendor instanceof HTMLSelectElement ? vendor.value : '',
      type: type instanceof HTMLSelectElement ? type.value : '',
      search: this.#normalizeSearchValue(search instanceof HTMLInputElement ? search.value : ''),
    };
  }

  /**
   * @param {string} value
   */
  #normalizeSearchValue(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '');
  }

  #visibleCount() {
    return this.#rows().filter((row) => !row.hidden).length;
  }

  #hasActiveFilters() {
    const { vendor, type, search } = this.#getFilterState();
    return Boolean(vendor || type || search);
  }

  #shouldLoadRemaining() {
    const { vendor, type, search } = this.#getFilterState();
    if (!this.#canLoadMore()) return false;
    if (vendor || type) return true;
    return search.length >= 2;
  }

  #canLoadMore() {
    const button = this.refs.loadMoreButton;
    return button instanceof HTMLButtonElement && Boolean(button.dataset.nextUrl) && button.isConnected;
  }

  #cancelFilterLoading() {
    this.#debouncedLoadRemaining?.cancel?.();
    this.#filterGeneration += 1;
    this.#filterLoadController?.abort();
    this.#filterLoadController = null;
    this.#autoLoadActive = false;
    this.#setLoadMoreBusy(false);
  }

  async #loadRemainingForFilters() {
    if (this.#autoLoadActive || !this.#shouldLoadRemaining()) {
      this.#updateEmptyState();
      return;
    }

    this.#autoLoadActive = true;
    const generation = ++this.#filterGeneration;
    this.#filterLoadController?.abort();
    const controller = new AbortController();
    this.#filterLoadController = controller;
    this.#updateEmptyState();

    try {
      while (this.#shouldLoadRemaining() && generation === this.#filterGeneration) {
        const loaded = await this.#fetchNextPage(controller.signal);
        if (!loaded || generation !== this.#filterGeneration) break;

        this.groupProductsByType();
        this.#syncTypeOptions();
        this.#filterRows();
        this.#updateProductCount();
        this.#updateEmptyState();
      }
    } finally {
      if (generation === this.#filterGeneration) {
        this.#autoLoadActive = false;
        this.#updateEmptyState();
        this.#setLoadMoreBusy(false);
      }
    }
  }

  /**
   * @param {AbortSignal} [signal]
   */
  async #fetchNextPage(signal) {
    if (this.#loadingMore) return false;

    const button = this.refs.loadMoreButton;
    if (!(button instanceof HTMLButtonElement)) return false;
    const nextUrl = button.dataset.nextUrl;
    if (!nextUrl) return false;

    this.#loadingMore = true;
    button.disabled = true;
    this.#setLoadMoreBusy(true);

    try {
      const url = new URL(nextUrl, window.location.origin);
      if (this.dataset.sectionId) url.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(url, signal ? { signal } : undefined);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newRows = doc.querySelectorAll('.quick-order-collection__row');
      const gridBody = this.refs.gridBody || this.querySelector('.quick-order-list__grid-body');
      if (!(gridBody instanceof HTMLElement)) return false;

      newRows.forEach((row) => gridBody.appendChild(row));

      const newButton = doc.querySelector('[ref="loadMoreButton"]');
      if (newButton instanceof HTMLButtonElement && newButton.dataset.nextUrl) {
        button.dataset.nextUrl = newButton.dataset.nextUrl;
        button.disabled = false;
      } else {
        button.removeAttribute('data-next-url');
        button.closest('.quick-order-collection__more')?.remove();
      }

      return true;
    } catch (error) {
      if (error?.name !== 'AbortError') {
        button.disabled = false;
        console.warn('[quick-order-collection] Failed to load more products:', error);
      }
      return false;
    } finally {
      this.#loadingMore = false;
    }
  }

  /**
   * @param {boolean} busy
   */
  #setLoadMoreBusy(busy) {
    const button = this.refs.loadMoreButton;
    const label = button?.querySelector('.button-text');
    if (label instanceof HTMLElement) {
      if (!label.dataset.defaultText) label.dataset.defaultText = label.textContent || '';
      label.textContent = busy && this.dataset.loadingMore ? this.dataset.loadingMore : label.dataset.defaultText;
    }
    button?.toggleAttribute('aria-busy', busy);
    this.classList.toggle('quick-order-collection--loading', busy);
  }

  #updateEmptyState() {
    const visible = this.#visibleCount();
    const loading = this.#autoLoadActive || (this.#shouldLoadRemaining() && this.#hasActiveFilters());
    const empty = visible === 0;
    this.classList.toggle('quick-order-collection--empty', empty);

    const emptyState = this.refs.emptyState;
    if (emptyState instanceof HTMLElement) {
      emptyState.hidden = !empty;
    }

    const emptyText = this.refs.emptyStateText;
    if (emptyText instanceof HTMLElement) {
      emptyText.textContent = loading && this.dataset.loadingMore ? this.dataset.loadingMore : this.dataset.emptyText || emptyText.textContent;
    }

    const clearButton = this.refs.clearFiltersButton;
    if (clearButton instanceof HTMLElement) {
      clearButton.hidden = !this.#hasActiveFilters();
    }
  }

  #updateProductCount() {
    const visible = this.#visibleCount();
    const text = this.#pluralize(visible, this.dataset.countOne, this.dataset.countOther);
    this.#setText(this.refs.productCountText, text);
  }

  /**
   * @param {number} count
   * @param {string | undefined} one
   * @param {string | undefined} other
   */
  #pluralize(count, one, other) {
    const template = count === 1 ? one : other;
    return (template || String(count)).replaceAll('__COUNT__', String(count));
  }

  /**
   * @param {number} cents
   */
  #formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.dataset.currency || 'CAD',
    }).format((Number(cents) || 0) / 100);
  }

  /**
   * @param {HTMLElement | undefined} element
   * @param {string} value
   */
  #setText(element, value) {
    if (element) element.textContent = value;
  }

  /**
   * @param {boolean} enabled
   */
  #toggleCartLinks(enabled) {
    for (const link of [this.refs.asideCartLink, this.refs.footerCartLink, this.refs.floatingCartLink]) {
      if (!(link instanceof HTMLElement)) continue;
      link.classList.toggle('is-disabled', !enabled);
      if (enabled) {
        link.removeAttribute('aria-disabled');
        link.removeAttribute('tabindex');
      } else {
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
      }
    }
  }

  /**
   * @param {boolean} show
   */
  #toggleConfirmationPanel(show) {
    this.refs.confirmationPanel.classList.toggle('hidden', !show);
    this.refs.totalInfo.classList.toggle('confirmation-visible', show);
  }

  /**
   * @param {string} message
   */
  #showErrorMessage(message) {
    this.refs.errorText.textContent = message;
    this.refs.errorContainer.classList.remove('hidden');
  }

  #clearErrorMessage() {
    this.refs.errorContainer.classList.add('hidden');
  }

  /**
   * @param {number} quantityAdded
   */
  #showSuccessMessage(quantityAdded) {
    this.#clearErrorMessage();
    const oneItemText = Theme?.translations?.items_added_to_cart_one || '1 item added to cart';
    const itemsText = Theme?.translations?.items_added_to_cart_other || '{{ count }} items added to cart';
    this.refs.successText.textContent =
      quantityAdded === 1 ? oneItemText : itemsText.replace('{{ count }}', quantityAdded.toString());
    this.refs.successContainer.classList.remove('hidden');
  }

  #clearSuccessMessage() {
    this.refs.successContainer.classList.add('hidden');
  }

  /**
   * @param {Array<string|number>} variantIds
   */
  #applyShimmerEffects(variantIds) {
    for (const variantId of variantIds) {
      const row = this.#rows().find((item) => item.dataset.variantId === String(variantId));
      const total = row?.querySelector('.variant-item__total-price');
      if (total && 'shimmer' in total && typeof total.shimmer === 'function') {
        total.shimmer();
      }
    }
    const totalPrice = this.querySelector('text-component[ref="totalPrice"]');
    if (totalPrice && 'shimmer' in totalPrice && typeof totalPrice.shimmer === 'function') {
      totalPrice.shimmer();
    }
  }

  #disableItems() {
    this.classList.add('quick-order-list-disabled');
  }

  #enableItems() {
    this.classList.remove('quick-order-list-disabled');
  }

  #observeFloatingSummary() {
    const aside = this.refs.asideTotals;
    const floating = this.refs.floatingSummary;
    if (!(aside instanceof HTMLElement) || !(floating instanceof HTMLElement)) return;

    this.#floatingObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const show = Boolean(entry && !entry.isIntersecting);
        floating.hidden = !show;
      },
      { threshold: 0 }
    );
    this.#floatingObserver.observe(aside);
  }
}

if (!customElements.get('quick-order-collection-component')) {
  customElements.define('quick-order-collection-component', QuickOrderCollectionComponent);
}
